import { useEffect, useLayoutEffect, useRef } from 'react';
import { getYaNative } from '@yanolja-org/ya-native';
import { SwipeBackParam } from '@yanolja-org/ya-native/dist/interfaces';

/** 웹뷰 스와이프백 기본값 - fixed : swipeback 미허용 */
export const DEFAULT_SWIPE_BACK: SwipeBackParam = { mode: 'fixed' };
/** 페이지 스와이프백 기본값 - back : window.history가 있는 경우 history back. 없는 경우 웹 인스턴스 닫기 */
export const DEFAULT_PAGE_SWIPE_BACK: SwipeBackParam = { mode: 'back' };
/** 전체 모달 스와이프백 기본값 - fixed : swipeback 미허용 */
export const DEFAULT_FULL_MODAL_SWIPE_BACK: SwipeBackParam = { mode: 'fixed' };
/**
 * IOS 스와이프 백 옵션 스택
 * 이전 설정값으로 복구하기 위해 사용
 */
let optionStacks: SwipeBackParam[] = [];

/**
 * IOS 스와이프백을 설정한다.
 *
 * @param option 스와이프백 설정 옵션
 */
const setSwipeBack = (option: SwipeBackParam): void => {
  const yaNative = getYaNative();
  if (!yaNative.isEnabled) {
    return;
  }
  yaNative.setSwipeBack(option);
  optionStacks.push(option);
};

/**
 * 이전 IOS 스와이프백 설정으로 복구한다.
 */
const restoreSwipeBack = (): void => {
  const yaNative = getYaNative();
  if (!yaNative.isEnabled) {
    return;
  }
  optionStacks.pop();
  const preOption = optionStacks[optionStacks.length - 1] ?? DEFAULT_SWIPE_BACK;
  yaNative.setSwipeBack(preOption);
};

/**
 * IOS 스와이프백 설정을 초기화한다.
 */
const resetSwipeBack = (): void => {
  const yaNative = getYaNative();
  if (!yaNative.isEnabled) {
    return;
  }
  optionStacks = [];
  yaNative.setSwipeBack(DEFAULT_SWIPE_BACK);
};

/**
 * 페이지 컴포넌트에서 사용되는 IOS 스와이프백 설정 훅
 *
 * @param option 스와이프백 설정 옵션 (default: {@link DEFAULT_PAGE_SWIPE_BACK})
 * @param isParentPage 부모 페이지인지 여부 (default: false)
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103744725 IOS 스와이프백 이동정책}
 */
export const usePageSwipeBack = (option?: SwipeBackParam, isParentPage?: boolean): void => {
  const effectFunction = isParentPage ? useLayoutEffect : useEffect; // 부모 페이지 > 자식 페이지 순으로 설정되도록 effect 함수 구분
  effectFunction(() => {
    resetSwipeBack();
    setSwipeBack(option ?? DEFAULT_PAGE_SWIPE_BACK);
    return () => resetSwipeBack();
  }, []);
};

/**
 * 전체 모달 컴포넌트에서 사용되는 IOS 스와이프백 설정 훅
 *
 * @param isOpen 모달이 열려있는지 여부
 * @param option 스와이프백 설정 옵션 (default: {@link DEFAULT_FULL_MODAL_SWIPE_BACK})
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103744725 IOS 스와이프백 이동정책}
 */
export const useFullModalSwipeBack = (isOpen: boolean, option?: SwipeBackParam): void => {
  const preIsOpen = useRef<boolean>(false);
  useEffect(() => {
    if (preIsOpen.current === isOpen) {
      return;
    }
    preIsOpen.current = isOpen;
    if (isOpen) {
      setSwipeBack(option ?? DEFAULT_FULL_MODAL_SWIPE_BACK);
    } else {
      restoreSwipeBack();
    }
  }, [isOpen]);

  // 오픈된 상태에서 바로 unmount 될 경우 예외 처리
  useEffect(() => {
    return () => {
      if (preIsOpen.current) {
        restoreSwipeBack();
      }
    };
  }, []);
};
